/**
 * Avatar — la foto de una persona, o sus iniciales si no hay.
 *
 * La foto la sube RRHH y la ve toda la organización (plan del portal, D4). Llega
 * como una URL firmada de vida corta: si vence con la pantalla abierta, el `<img>`
 * falla y el avatar **cae a las iniciales** en vez de mostrar una imagen rota.
 *
 * ⚠️ `decorativa`: cuando el nombre ya está escrito al lado (la barra superior, una
 * fila del organigrama), la foto no agrega información y un lector de pantalla
 * leería el nombre dos veces. En ese caso va con `alt=""` y oculta.
 */
import { useEffect, useState } from 'react'
import { iniciales } from './iniciales'

const TAMANOS = { sm: 32, md: 48, lg: 88, xl: 132 } as const
export type AvatarTamano = keyof typeof TAMANOS

export interface AvatarProps {
  nombre: string
  fotoUrl?: string | null
  tamano?: AvatarTamano
  /** El nombre ya está escrito al lado: la foto no se anuncia. */
  decorativa?: boolean
  className?: string
}

export function Avatar({ nombre, fotoUrl, tamano = 'md', decorativa = false, className = '' }: AvatarProps) {
  const [fallo, setFallo] = useState(false)
  // Una URL nueva (se cambió la foto, o se renovó la firma) merece otro intento.
  useEffect(() => { setFallo(false) }, [fotoUrl])

  const px = TAMANOS[tamano]
  const base = `inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ${className}`.trim()
  const estilo = { width: px, height: px }

  if (fotoUrl && !fallo) {
    return (
      <img
        src={fotoUrl}
        alt={decorativa ? '' : `Foto de ${nombre}`}
        aria-hidden={decorativa || undefined}
        onError={() => setFallo(true)}
        className={`${base} object-cover bg-primary-100`}
        style={estilo}
        width={px}
        height={px}
      />
    )
  }
  return (
    <span
      className={`${base} bg-primary-100 text-primary-hover font-extrabold select-none`}
      style={{ ...estilo, fontSize: Math.round(px * 0.36) }}
      {...(decorativa ? { 'aria-hidden': true } : { role: 'img', 'aria-label': nombre })}
    >
      {iniciales(nombre)}
    </span>
  )
}
